import z from "zod";

import { ARABIC_ENGLISH_ADDRESS_EXTENDED_REGEX } from "@/utils/constants/arabicRegex";
import { PHONE_INPUT_REGEX } from "@/utils/constants/phoneRegex";

const ibanRegex = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/;
const alphanumericRegex = /^[a-zA-Z0-9]+$/;

// Basic information (details)
export const legalEntityDetailsSchema = z.object({
  name: z.string().min(1, "Name must be at least 1 characters").max(100),
  commercialRegistration: z
    .string()
    .min(1, "CR number must be at least 1 characters")
    .max(50, "CR number must not exceed 50 characters")
    .refine(val => alphanumericRegex.test(val), {
      message: "CR number must be alphanumeric",
    }),
  VAT: z
    .string()
    .min(1, "VAT number must be at least 1 characters")
    .max(15, "VAT number must not exceed 15 characters")
    .refine(val => alphanumericRegex.test(val), {
      message: "VAT number must be alphanumeric",
    }),
  ibanNumber: z
    .string()
    .min(1, "IBAN number must be at least 1 characters")
    .refine(val => ibanRegex.test(val.replace(/\s+/g, "")), {
      message: "Enter a valid IBAN number",
    }),
  country: z.string().min(1, "Country is required"),
  cityId: z
    .string()
    .min(1, "City is required")
    .refine(val => val !== "", { message: "City cannot be empty" }),
  address: z
    .string()
    .min(1, { message: "Address is required." })
    .max(300, { message: "Address must be under 300 characters." })
    .regex(ARABIC_ENGLISH_ADDRESS_EXTENDED_REGEX, {
      message: "Invalid characters in address.",
    })
    .trim(),
  email: z
    .string()
    .min(1, { message: "This field is required" })
    .email({ message: "Enter a valid contact email" }),
  phone: z
    .string()
    .min(1, { message: "Phone number is required." })
    .refine(val => val === null || val === "" || PHONE_INPUT_REGEX.test(val), {
      message: "Invalid phone number format",
    })
    .refine(
      val => {
        if (!val) return true;
        return val.replace(/\D/g, "").length <= 15;
      },
      {
        message: "Phone number must not exceed 15 digits",
      }
    )
    .refine(
      val => {
        return val !== "+";
      },
      {
        message: "Phone number cannot be just '+'",
      }
    ),
  isFundable: z.boolean().optional(),
  id: z.string().optional(),
  cityName: z.string().optional(),
  active: z.boolean().optional(),
});

export type LegalEntityDetailsType = z.infer<typeof legalEntityDetailsSchema>;

// System linking
export const legalEntitySystemLinkingSchema = z.object({
  parentOfId: z.string().min(1).optional().nullable(),
  linkedGroupIds: z.string().min(1).optional().nullable(),
  linkedBrandIds: z.string().min(1).optional().nullable(),
  linkedBranchIds: z.string().min(1).optional().nullable(),
});

export type LegalEntitySystemLinkingType = z.infer<
  typeof legalEntitySystemLinkingSchema
>;

export const newLegalEntitySchema = legalEntityDetailsSchema.merge(
  legalEntitySystemLinkingSchema
);

export type NewLegalEntityFormType = z.infer<typeof newLegalEntitySchema>;
